package co.maytes.standupbot.dashboard.features.standupruns

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonSize
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.ControlSize
import co.maytes.standupbot.dashboard.components.widgets.Select
import co.maytes.standupbot.dashboard.components.widgets.SelectOption
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideX
import co.maytes.standupbot.dashboard.lib.api.ApiClient
import co.maytes.standupbot.dashboard.lib.api.Result
import co.maytes.standupbot.dashboard.styles.Motion
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.dto.standuprun.StandupRunStatusDto
import co.maytes.standupbot.shared.dto.standup.StandupDto
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.top
import com.varabyte.kobweb.compose.ui.modifiers.zIndex
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px

@Composable
fun StandupFilterBar(
    standups: List<StandupDto>,
    current: StandupFilterState,
    onChange: (StandupFilterState) -> Unit,
    onClear: () -> Unit,
) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .position(Position.Sticky)
            .top(56.px)
            .zIndex(10)
            .backgroundColor(Palette.background)
            .padding(topBottom = Spacing.sm)
            .styleModifier {
                property("border-bottom", "1px solid ${Palette.border.filterRgb()}")
                property("backdrop-filter", "saturate(180%) blur(6px)")
            },
    ) {
        Column(modifier = Modifier.fillMaxWidth()) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(Spacing.md),
            ) {
                TeamDropdown(
                    standups = standups,
                    selectedId = current.standupId,
                    onSelect = { onChange(current.copy(standupId = it)) },
                )
                DateInput(
                    label = "From",
                    valueIso = current.scheduledAfterIso,
                    onChange = { onChange(current.copy(scheduledAfterIso = it)) },
                )
                DateInput(
                    label = "To",
                    valueIso = current.scheduledBeforeIso,
                    onChange = { onChange(current.copy(scheduledBeforeIso = it)) },
                )
                if (!current.isEmpty) {
                    Button(
                        label = "Clear filters",
                        variant = ButtonVariant.Ghost,
                        size = ButtonSize.Sm,
                        onClick = onClear,
                    )
                }
            }
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .margin(top = Spacing.sm),
                horizontalArrangement = Arrangement.spacedBy(Spacing.xs),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                StatusPills(
                    selected = current.status,
                    onToggle = { onChange(current.copy(status = if (current.status == it) null else it)) },
                )
            }
            if (!current.isEmpty) {
                ActiveFilterChips(
                    standups = standups,
                    current = current,
                    onChange = onChange,
                )
            }
        }
    }
}

@Composable
private fun ActiveFilterChips(
    standups: List<StandupDto>,
    current: StandupFilterState,
    onChange: (StandupFilterState) -> Unit,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .margin(top = Spacing.sm)
            .styleModifier { property("flex-wrap", "wrap") },
        horizontalArrangement = Arrangement.spacedBy(Spacing.xs),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        current.standupId?.let { id ->
            val name = standups.firstOrNull { it.id == id }?.name ?: "Team"
            DismissibleChip(
                label = name,
                onRemove = { onChange(current.copy(standupId = null)) },
            )
        }
        current.scheduledAfterIso?.let { iso ->
            DismissibleChip(
                label = "From ${iso.take(10)}",
                onRemove = { onChange(current.copy(scheduledAfterIso = null)) },
            )
        }
        current.scheduledBeforeIso?.let { iso ->
            DismissibleChip(
                label = "To ${iso.take(10)}",
                onRemove = { onChange(current.copy(scheduledBeforeIso = null)) },
            )
        }
        current.status?.let { status ->
            DismissibleChip(
                label = statusLabel(status),
                onRemove = { onChange(current.copy(status = null)) },
            )
        }
    }
}

@Composable
private fun DismissibleChip(label: String, onRemove: () -> Unit) {
    Row(
        modifier = Modifier
            .padding(topBottom = 3.px, leftRight = Spacing.xs)
            .borderRadius(Radius.full)
            .cursor(Cursor.Pointer)
            .backgroundColor(Palette.accentSubtle)
            .color(Palette.accentHover)
            .styleModifier {
                property("border", "1px solid ${Palette.accentBorder.filterRgb()}")
                property("transition", Motion.TRANSITION_BASE)
                property("user-select", "none")
                property("gap", "4px")
            }
            .onClick { onRemove() },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        SpanText(
            text = label,
            modifier = Modifier
                .fontSize(Typography.caption.size)
                .fontWeight(500),
        )
        LucideX(size = 12)
    }
}

private fun statusLabel(status: StandupRunStatusDto): String = when (status) {
    StandupRunStatusDto.POSTED -> "Posted"
    StandupRunStatusDto.FAILED -> "Failed"
    StandupRunStatusDto.NO_ACTIVITY -> "No activity"
    StandupRunStatusDto.PENDING -> "Pending"
    StandupRunStatusDto.GENERATING -> "Generating"
    StandupRunStatusDto.DRAFTED -> "Draft"
    StandupRunStatusDto.SKIPPED -> "Skipped"
}

@Composable
private fun TeamDropdown(
    standups: List<StandupDto>,
    selectedId: String?,
    onSelect: (String?) -> Unit,
) {
    Select(
        value = selectedId,
        options = listOf(SelectOption<String?>(null, "All teams")) +
            standups.map { SelectOption(it.id, it.name) },
        onValueChange = onSelect,
        modifier = Modifier
            .styleModifier { property("min-width", "180px") },
        size = ControlSize.Sm,
    )
}

@Composable
private fun StatusPills(
    selected: StandupRunStatusDto?,
    onToggle: (StandupRunStatusDto) -> Unit,
) {
    val options = listOf(
        StandupRunStatusDto.POSTED to "Posted",
        StandupRunStatusDto.DRAFTED to "Draft",
        StandupRunStatusDto.FAILED to "Failed",
        StandupRunStatusDto.NO_ACTIVITY to "No activity",
    )
    options.forEach { (status, label) ->
        val isActive = selected == status
        Box(
            modifier = Modifier
                .padding(topBottom = 4.px, leftRight = Spacing.sm)
                .borderRadius(Radius.full)
                .cursor(Cursor.Pointer)
                .backgroundColor(if (isActive) Palette.accent else Palette.surface)
                .color(if (isActive) Color.rgb(0xFFFFFF) else Palette.textSecondary)
                .styleModifier {
                    property(
                        "border",
                        "1px solid ${if (isActive) Palette.accent.filterRgb() else Palette.border.filterRgb()}",
                    )
                    property("transition", Motion.TRANSITION_BASE)
                    property("user-select", "none")
                }
                .onClick { onToggle(status) },
        ) {
            SpanText(
                text = label,
                modifier = Modifier
                    .fontSize(Typography.caption.size)
                    .fontWeight(500),
            )
        }
    }
}

@Composable
private fun DateInput(
    label: String,
    valueIso: String?,
    onChange: (String?) -> Unit,
) {
    val dateOnly = remember(valueIso) { valueIso?.take(10) ?: "" }
    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(Spacing.xs)) {
        SpanText(
            text = label,
            modifier = Modifier
                .fontSize(Typography.caption.size)
                .color(Palette.textSecondary),
        )
        co.maytes.standupbot.dashboard.components.widgets.DatePicker(
            value = dateOnly,
            onChange = { raw ->
                val normalized = if (raw.isBlank()) null else "${raw}T00:00:00Z"
                onChange(normalized)
            },
            widthPx = 150,
        )
    }
}

private fun org.jetbrains.compose.web.css.CSSColorValue.filterRgb(): String = toString()

suspend fun fetchWorkspaceTeams(api: ApiClient, workspaceId: String): List<StandupDto> =
    when (val res = api.get<List<StandupDto>>("/api/v1/workspaces/$workspaceId/standups")) {
        is Result.Ok -> res.value
        is Result.Err -> emptyList()
    }

@Composable
fun rememberTeams(api: ApiClient, workspaceId: String?): List<StandupDto> {
    var standups by remember { mutableStateOf(emptyList<StandupDto>()) }
    LaunchedEffect(workspaceId) {
        if (workspaceId.isNullOrBlank()) return@LaunchedEffect
        standups = fetchWorkspaceTeams(api, workspaceId)
    }
    return standups
}
